/**
 * PasswordField — heslo s okamžitými nápovědami a kontrolou na serveru.
 *
 * - pole s ukázat/skrýt (u `readOnly` jen kontrola, hodnotu kreslí volající),
 * - okamžité nápovědy bez sítě: minimum (`minLength` od serveru — politika adresáře),
 *   login/jméno/mail z `context`, řady a opakování, volitelně složitost AD,
 * - tlačítko Zkontrolovat → `passwords.check` (s kontextem a minimem), verdikt + důvody,
 * - `leak_checked: false` podle `mode`: `create` = upozornění, uložení se zeptá na souhlas;
 *   `change` = blokuje (server takové heslo nenastaví),
 * - `onResult` dává formuláři stav, aby mohl blokovat uložení.
 *
 * Heslo se nikam neukládá ani neloguje; jde jen do `passwords.check`.
 */
import { useEffect, useState } from 'react';
import { Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { mcpCall } from '../../lib/mcp';
import {
  PASSWORD_FALLBACK_MIN_LENGTH,
  type PasswordHintCode,
  passwordHints,
} from '../../lib/password-hints';
import { Alert } from './Alert';
import { Badge } from './Badge';
import { Button } from './Button';
import { Input } from './Input';
import { Label } from './Label';

export interface PasswordReasonView {
  code: string;
  message_cs: string;
  level: 'block' | 'warn';
}

export interface PasswordCheckResult {
  ok?: boolean;
  error?: string;
  code?: string;
  verdict?: 'ok' | 'weak' | 'leaked';
  reasons?: PasswordReasonView[];
  leak_checked?: boolean;
  min_length?: number;
  min_length_source?: string | null;
}

export interface PasswordContext {
  login?: string;
  names?: string[];
  emails?: string[];
}

export interface PasswordFieldState {
  /** Okamžité nápovědy, které heslo nesplňuje. */
  problems: PasswordHintCode[];
  /** Poslední kontrola na serveru (null = neproběhla pro tuhle hodnotu). */
  check: PasswordCheckResult | null;
  /** Formulář by neměl ukládat. */
  blocking: boolean;
}

export interface PasswordFieldProps {
  value: string;
  onChange?: (value: string) => void;
  /** Hodnotu kreslí volající (vygenerované heslo) — jen nápovědy ke kontrole a tlačítko. */
  readOnly?: boolean;
  label?: string;
  context?: PasswordContext;
  /** Minimum od serveru (politika adresáře). Bez něj náhradních 8. */
  minLength?: number;
  minLengthSource?: string | null;
  requireComplexity?: boolean;
  /** create = nový účet (neověřené heslo jde se souhlasem), change = změna hesla. */
  mode?: 'create' | 'change';
  /** Vlastní volání kontroly (např. přes uzel widgetu). Výchozí `passwords.check` přes MCP. */
  check?: (args: Record<string, unknown>) => Promise<PasswordCheckResult>;
  onResult?: (state: PasswordFieldState) => void;
  /** Důvody, proč server heslo při uložení odmítl. */
  serverReasons?: PasswordReasonView[] | null;
}

const HINTS: PasswordHintCode[] = [
  'length',
  'complexity',
  'contains_login',
  'contains_name',
  'contains_email',
  'sequence',
  'repetition',
];

function ReasonList({ reasons }: { reasons: PasswordReasonView[] }) {
  if (reasons.length === 0) return null;
  return (
    <ul className='mt-1 list-disc space-y-0.5 pl-5 text-sm'>
      {reasons.map((reason) => <li key={reason.code}>{reason.message_cs}</li>)}
    </ul>
  );
}

export function PasswordField({
  value,
  onChange,
  readOnly = false,
  label,
  context,
  minLength,
  minLengthSource,
  requireComplexity = false,
  mode = 'change',
  check,
  onResult,
  serverReasons,
}: PasswordFieldProps) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [result, setResult] = useState<PasswordCheckResult | null>(null);
  const [checking, setChecking] = useState(false);
  const min = minLength ?? PASSWORD_FALLBACK_MIN_LENGTH;
  const contextKey = JSON.stringify(context ?? {});
  const problems = passwordHints(value, { ...context, minLength: min, requireComplexity });

  // Výsledek patří k jedné hodnotě a jednomu kontextu.
  useEffect(() => setResult(null), [value, contextKey, min]);

  const unverified = result?.ok !== false && result?.verdict === 'ok' &&
    result.leak_checked === false;
  const blocking = (!readOnly && problems.length > 0) ||
    (result?.ok !== false && result?.verdict !== undefined && result.verdict !== 'ok') ||
    (unverified && mode === 'change');

  useEffect(() => {
    onResult?.({ problems, check: result, blocking });
    // problems se počítá z hodnoty a kontextu, které jsou v závislostech.
  }, [value, contextKey, min, result, blocking]);

  const runCheck = async () => {
    if (!value) return;
    setChecking(true);
    const args: Record<string, unknown> = {
      password: value,
      login: context?.login || undefined,
      names: (context?.names ?? []).filter(Boolean),
      emails: (context?.emails ?? []).filter(Boolean),
      ...(minLength ? { min_length: minLength } : {}),
      ...(minLength && minLengthSource ? { min_length_source: minLengthSource } : {}),
    };
    try {
      setResult(await (check ?? ((a) => mcpCall<PasswordCheckResult>('passwords.check', a)))(args));
    } catch (error) {
      setResult({ ok: false, error: (error as Error).message });
    } finally {
      setChecking(false);
    }
  };

  const hintLabel = (code: PasswordHintCode) => {
    if (code === 'length') {
      return minLengthSource
        ? t('passwords.hintLengthSource', { count: min, source: minLengthSource })
        : t('passwords.hintLength', { count: min });
    }
    return t(`passwords.hint_${code}`);
  };

  const blockingReasons = (result?.reasons ?? []).filter((reason) => reason.level === 'block');
  const warnings = (result?.reasons ?? []).filter((reason) => reason.level !== 'block');

  return (
    <div className='space-y-2'>
      {!readOnly && (
        <>
          {label && <Label>{label}</Label>}
          <div className='flex gap-2'>
            <Input
              type={visible ? 'text' : 'password'}
              autoComplete='new-password'
              value={value}
              onChange={(event) => onChange?.(event.target.value)}
            />
            <Button
              variant='ghost'
              size='md'
              className='h-10'
              icon={visible ? EyeOff : Eye}
              onClick={() => setVisible(!visible)}
              aria-label={visible ? t('passwords.hide') : t('passwords.show')}
            />
          </div>
          <div className='flex flex-wrap gap-1.5'>
            {HINTS.filter((code) => code !== 'complexity' || requireComplexity).map((code) => (
              <Badge
                key={code}
                variant={value.length > 0 && !problems.includes(code) ? 'success' : 'default'}
              >
                {hintLabel(code)}
              </Badge>
            ))}
          </div>
        </>
      )}

      {value && (
        <Button
          variant='secondary'
          size='md'
          className='h-10'
          icon={ShieldCheck}
          loading={checking}
          onClick={() => void runCheck()}
        >
          {t('passwords.fieldCheck')}
        </Button>
      )}

      {result && result.ok === false && (
        <Alert variant='warning'>{result.error || t('passwords.fieldFailed')}</Alert>
      )}
      {result && result.ok !== false && (
        <Alert
          variant={result.verdict === 'ok' ? (unverified ? 'warning' : 'success') : 'destructive'}
        >
          <p className='text-sm font-medium'>
            {result.verdict === 'leaked'
              ? t('passwords.fieldLeaked')
              : result.verdict === 'weak'
              ? t('passwords.fieldWeak')
              : unverified
              ? (mode === 'create'
                ? t('passwords.fieldUnverifiedCreate')
                : t('passwords.fieldUnverifiedChange'))
              : t('passwords.fieldOk')}
          </p>
          <ReasonList reasons={blockingReasons} />
          {warnings.map((reason) => (
            <p key={reason.code} className='mt-1 text-xs text-muted-foreground'>
              {reason.message_cs}
            </p>
          ))}
        </Alert>
      )}

      {serverReasons && serverReasons.length > 0 && (
        <Alert variant='destructive'>
          <p className='text-sm font-medium'>{t('passwords.fieldRejected')}</p>
          <ReasonList reasons={serverReasons} />
        </Alert>
      )}
    </div>
  );
}
